import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, Typography, AccordionDetails, Grid, styled } from '@mui/material';
import { ReactNode } from 'react';
import { logKariusEvent, EngagementEvents } from '../Analytics';
import Colors from '../theme/colors';

interface HelpAccordionProps {
  title: string;
  details: string | ReactNode;
  index: number;
}

export const LoginHelpSection = () => {
  return (
    <Grid
      justifyContent='center'
      flexDirection='column'
      sx={{
        maxWidth: '428px',
        padding: {
          md: '0px',
          xs: '0px 31px',
        },
      }}
    >
      <Typography variant='body1' sx={{ fontSize: '18px', margin: '0 0 10px 38px', color: Colors.blue }}>
        Need help?
      </Typography>
      <Grid container flexDirection='column' sx={{ gap: '10px' }}>
        <HelpAccordion
          index={1}
          title='Setting up or signing in to your account'
          details={
            <>
              If your institution doesn’t have an account yet with Karius, you need a digital user account for the
              Karius Portal or Karius Mobile App, or need help signing in to your account, Karius is happy to assist
              you:{' '}
              <a href={'tel:8664527487'} onClick={() => logLink('phone')} className='link-text'>
                (866) 452-7487
              </a>{' '}
              or{' '}
              <a href={'mailto:help@kariusdx.com'} onClick={() => logLink('email')} className='link-text'>
                help@kariusdx.com
              </a>
              .
            </>
          }
        />
        <HelpAccordion
          index={2}
          title='How to send specimens'
          details={
            <>
              Please see the{' '}
              <a
                href='https://karius.showpad.com/share/eMdS8q9YeDVU1NyoHG4aS'
                onClick={() => logLink('specimen_instructions')}
                target='_blank'
                rel='noreferrer'
                className='link-text'
              >
                Karius Spectrum Specimen Collection, Preparation, and Shipping Instructions.
              </a>
            </>
          }
        />
        <HelpAccordion
          index={3}
          title='When are specimens processed?'
          details='Specimens are received Monday-Saturday and results are reported Tuesday-Sunday.'
        />
        <HelpAccordion
          index={4}
          title='Why doesn’t the Karius Spectrum test detect the coronavirus?'
          details={
            <>
              The Karius Spectrum test detects microbial cell-free DNA from bacteria, DNA viruses, fungi, and parasites.
              It does not currently detect RNA viruses such as the coronavirus. Our{' '}
              <a
                href='https://kariusdx.com/our-solution/karius-spectrum'
                onClick={() => logLink('clinical_faq')}
                target='_blank'
                rel='noreferrer'
                className='link-text'
              >
                Clinical FAQs
              </a>{' '}
              offer more detail on potential applications for the test.
            </>
          }
        />
      </Grid>
    </Grid>
  );
};

const HelpAccordion = ({ title, details, index }: HelpAccordionProps) => {
  return (
    <StyledAccordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}a-content`}
        id={`panel${index}a-header`}
        onClick={() => logAccordion(title)}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: Colors.teal,
            fontWeight: 500,
            lineHeight: '21px',
            fontStyle: 'normal',
            fontFamily: 'Roboto',
          }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography
          sx={{
            color: Colors.blue,
            fontSize: '14px',
            lineHeight: '19px',
          }}
        >
          {details}
        </Typography>
      </AccordionDetails>
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  '&.MuiAccordion-root': {
    boxShadow: 'none',
    margin: '0px',
    '&:before': {
      backgroundColor: Colors.white,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      '& svg': {
        color: Colors.teal,
      },
    },
    '& .MuiAccordionSummary-content': {
      margin: '0px 0px 0px 16px',
    },
    '& .MuiAccordionSummary-root': {
      flexDirection: 'row-reverse',
      padding: theme.spacing(0),
      minHeight: '0px',
      marginBottom: '10px',
    },
    '& .MuiAccordionDetails-root': {
      padding: '0 4px 9px 40px',
      '&.Mui-expanded': {
        minHeight: 0,
        height: '26px',
      },
    },
  },
}));

const logAccordion = (title: string) => {
  logKariusEvent(EngagementEvents.need_help_accordion_tapped, { title });
};

const logLink = (link: 'phone' | 'email' | 'clinical_faq' | 'specimen_instructions') => {
  logKariusEvent(EngagementEvents.need_help_link_tapped, { link });
};
